import React, { useCallback, useEffect, useRef, useState } from "react";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { useDebouncedCallback } from "@me8eon/debounce";
import { useTranslation } from "@me8eon/translation";
import { fetchIncidentCategoryDetails, IncidentCategoryDetail, DashboardRange } from "../../../services/dashboardApi";
import { fmt, currentLocale } from "../poData";

type Props = {
    parent: string;
    child: string;
    label: string;
    range: DashboardRange;
    companies?: string[];
    search?: string;
    onClose: () => void;
};

const PAGE = 50;
// Toggling the company filter while this modal is open fires one state change per click —
// debounce the actual refetch so a burst of clicks collapses into one request.
const FILTER_DEBOUNCE_MS = 350;

const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(currentLocale(), { year: "numeric", month: "short", day: "numeric" });
};

/** Drill-down modal (popup): lists the individual incidents for one category, newest-first, paginated. */
const IncidentCategoryPanel: React.FC<Props> = ({ parent, child, label, range, companies, search, onClose }) => {
    const t = useTranslation();
    const tp = (key: string) => t(`productDashboard.incidentCategory.${key}`);
    const ti = (key: string) => t(`productDashboard.incidents.${key}`);

    const idToken = useJwtToken();
    // `idToken` is a fn — always truthy once useJwtToken() returns, even before the user is
    // actually authenticated, so it can't signal "auth is ready". UserData.loggedIn can.
    const { loggedIn } = useUserData();
    const [items, setItems] = useState<IncidentCategoryDetail[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Close on Esc + lock body scroll while the modal is open (mount/unmount only).
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onCloseRef.current();
        };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, []);

    // (Re)load the first page whenever the selected category (or range/company filter) changes.
    // The refetch is debounced (FILTER_DEBOUNCE_MS) for changes AFTER the panel opens; the first
    // load (mount, or once actually logged in) runs immediately via a separate, non-debounced
    // effect below, so loggedIn/idToken never drive (or reset) the debounce itself.
    const rangeKey = "from" in range ? `r:${range.from}:${range.to}` : `w:${range.window}`;
    const companiesKey = (companies ?? []).join(",");
    const latestRef = useRef({ range, idToken, companies, search, loggedIn });
    latestRef.current = { range, idToken, companies, search, loggedIn };
    const requestIdRef = useRef(0);
    const mountedRef = useRef(true);
    useEffect(() => {
        // Reset on setup too (not just rely on the initial useRef value) — React StrictMode's dev
        // double-invoke runs setup -> cleanup -> setup again for the same mount, so without this
        // the cleanup below would leave mountedRef stuck at false even though still mounted.
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    // Key of last params we fetched — null until the first fetch.
    const lastFetchKeyRef = useRef<string | null>(null);

    const runFetch = useCallback(() => {
        const { range, idToken, companies, search, loggedIn } = latestRef.current;
        if (!loggedIn) return; // not actually authenticated yet — idToken() would just fail
        const requestId = ++requestIdRef.current;
        setItems([]);
        fetchIncidentCategoryDetails(range, parent, child, idToken, PAGE, 0, companies, search)
            .then((r) => {
                if (!mountedRef.current || requestIdRef.current !== requestId) return; // superseded or unmounted
                setItems(r.items);
                setTotal(r.total);
            })
            .catch(() => mountedRef.current && requestIdRef.current === requestId && setError(true))
            .finally(() => mountedRef.current && requestIdRef.current === requestId && setLoading(false));
        // loggedIn deliberately excluded from this callback's OWN deps — runFetch reads the live
        // value from latestRef, and auth readiness is handled by the effect below.
    }, [parent, child, rangeKey, companiesKey, search]);

    const debouncedFetch = useDebouncedCallback(runFetch, FILTER_DEBOUNCE_MS);

    // Single effect for all fetches (immediate first load, then debounced on change) — splitting
    // this into two effects previously double-fired on mount, since both ran in the same commit.
    useEffect(() => {
        if (!loggedIn) return;
        const key = `${parent}|${child}|${rangeKey}|${companiesKey}|${search ?? ""}`;
        if (lastFetchKeyRef.current === key) return; // already fetched/in-flight
        const isFirstLoad = lastFetchKeyRef.current === null;
        lastFetchKeyRef.current = key;
        setLoading(true);
        setError(false);
        if (isFirstLoad) runFetch();
        else debouncedFetch();
    }, [loggedIn, parent, child, rangeKey, companiesKey, search, runFetch, debouncedFetch]);

    const loadMore = () => {
        setLoading(true);
        fetchIncidentCategoryDetails(range, parent, child, idToken, PAGE, items.length, companies, search)
            .then((r) => {
                setItems((prev) => [...prev, ...r.items]);
                setTotal(r.total);
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    return (
        <div className="po-modal" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="po-modal__dialog" onClick={(e) => e.stopPropagation()}>
                <div className="po-modal__head">
                    <div>
                        <h3>{tp("titlePrefix")} {label}</h3>
                        <p className="po-modal__sub">
                            {error ? tp("loadError") : `${fmt(total)} ${total === 1 ? tp("incident") : tp("incidents")}`}
                        </p>
                    </div>
                    <div className="po-modal__head-actions">
                        <button type="button" className="po-modal__close" onClick={onClose} aria-label={tp("closeAria")}>
                            ✕
                        </button>
                    </div>
                </div>

                <div className="po-modal__body">
                    {!error && (
                        <table className="po-table">
                            <thead>
                                <tr>
                                    <th>{ti("thId")}</th>
                                    <th>{ti("thIssue")}</th>
                                    <th>{tp("thDate")}</th>
                                    <th>{ti("thProduct")}</th>
                                    <th>{ti("thCompany")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((inc) => (
                                    <tr key={inc.id}>
                                        <td className="po-table__id">{inc.id}</td>
                                        <td className="po-table__strong">{inc.issue || "—"}</td>
                                        <td className="po-table__muted">{fmtDate(inc.openedAt)}</td>
                                        <td className="po-table__muted">{inc.product || "—"}</td>
                                        <td className="po-table__muted">{inc.company || "—"}</td>
                                    </tr>
                                ))}
                                {!loading && items.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="po-table__muted">
                                            {tp("empty")}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}

                    {loading && <p className="po-modal__sub" style={{ padding: "12px 4px" }}>{tp("loading")}</p>}

                    {!loading && !error && items.length < total && (
                        <button type="button" className="po-link" style={{ marginTop: 12 }} onClick={loadMore}>
                            {tp("loadMorePrefix")} {fmt(items.length)} {tp("of")} {fmt(total)}) →
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IncidentCategoryPanel;
